/**
 * three.js BufferGeometry builders for Voronoi FractureCells.
 * Fan-triangulate convex faces; flat normals; two material groups:
 *   group 0 = outer (original box surface)
 *   group 1 = interior fracture faces
 */

import * as THREE from 'three';
import type { FractureCell, Vec3 } from './voronoiFracture';
import { SURFACE_REST_POSITION_ATTRIBUTE } from '../../materials/core/SurfaceCoordinates';

/** Original wall-local coordinates shared by every custom surface shader. */
export { SURFACE_REST_POSITION_ATTRIBUTE } from '../../materials/core/SurfaceCoordinates';

export const DEFAULT_WALL_SIZE = { width: 6, height: 4.2, thickness: 0.6 };
export const EDGE_AO_FALLOFF_M = 0.022;
/** Rest-space metres covered by one 1024² interior tile. */
export const INTERIOR_TILE_METERS = 2;

export type BoxFaceId = 'px' | 'nx' | 'py' | 'ny' | 'pz' | 'nz';
export type CellBakeFaceId = 'front' | 'right' | 'top';

export interface AtlasRegion {
  id: CellBakeFaceId;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface AtlasRegions {
  front: AtlasRegion;
  right: AtlasRegion;
  top: AtlasRegion;
}

function atlasUvFromFace(faceU: number, faceV: number, region: AtlasRegion): [number, number] {
  const u = Math.min(1, Math.max(0, faceU));
  const v = Math.min(1, Math.max(0, faceV));
  return [region.u0 + (region.u1 - region.u0) * u, region.v0 + (region.v1 - region.v0) * v];
}

const IDENTITY_ATLAS: AtlasRegions = {
  front: { id: 'front', u0: 0, v0: 0, u1: 1, v1: 1 },
  right: { id: 'right', u0: 0, v0: 0, u1: 1, v1: 1 },
  top: { id: 'top', u0: 0, v0: 0, u1: 1, v1: 1 },
};

export function boxFaceFromNormal(nx: number, ny: number, nz: number): BoxFaceId {
  const ax = Math.abs(nx);
  const ay = Math.abs(ny);
  const az = Math.abs(nz);
  if (ax >= ay && ax >= az) return nx >= 0 ? 'px' : 'nx';
  if (ay >= ax && ay >= az) return ny >= 0 ? 'py' : 'ny';
  return nz >= 0 ? 'pz' : 'nz';
}

export function bakeFaceFromBoxFace(face: BoxFaceId): CellBakeFaceId {
  if (face === 'px' || face === 'nx') return 'right';
  if (face === 'py' || face === 'ny') return 'top';
  return 'front';
}

export function restToFaceUv(
  rx: number,
  ry: number,
  rz: number,
  face: BoxFaceId,
  wall: { width: number; height: number; thickness: number },
): [number, number] {
  const w = Math.max(wall.width, 1e-6);
  const h = Math.max(wall.height, 1e-6);
  const t = Math.max(wall.thickness, 1e-6);
  switch (face) {
    case 'pz':
      return [rx / w + 0.5, ry / h + 0.5];
    case 'nz':
      return [-rx / w + 0.5, ry / h + 0.5];
    case 'px':
      return [-rz / t + 0.5, ry / h + 0.5];
    case 'nx':
      return [rz / t + 0.5, ry / h + 0.5];
    case 'py':
      return [rx / w + 0.5, -rz / t + 0.5];
    case 'ny':
      return [rx / w + 0.5, rz / t + 0.5];
  }
}

export function restToInteriorUv(
  rx: number,
  ry: number,
  rz: number,
  nx: number,
  ny: number,
  nz: number,
  tileMeters: number,
): [number, number] {
  const tile = Math.max(tileMeters, 1e-6);
  const nlen = Math.hypot(nx, ny, nz) || 1;
  const nnx = nx / nlen;
  const nny = ny / nlen;
  const nnz = nz / nlen;
  let tx: number;
  let ty: number;
  let tz: number;
  if (Math.abs(nny) < 0.9) {
    tx = nny * 0 - 1 * nnz;
    ty = nnz * 0 - 0 * nnx;
    tz = nnx * 1 - 0 * nny;
  } else {
    tx = nny * 0 - 0 * nnz;
    ty = nnz * 1 - 0 * nnx;
    tz = nnx * 0 - 1 * nny;
  }
  const tlen = Math.hypot(tx, ty, tz) || 1;
  tx /= tlen;
  ty /= tlen;
  tz /= tlen;
  const bx = nny * tz - nnz * ty;
  const by = nnz * tx - nnx * tz;
  const bz = nnx * ty - nny * tx;
  return [(rx * tx + ry * ty + rz * tz) / tile, (rx * bx + ry * by + rz * bz) / tile];
}

function onWallSkin(
  x: number,
  y: number,
  z: number,
  wall: { width: number; height: number; thickness: number },
  eps = 0.002,
): boolean {
  return (
    Math.abs(Math.abs(x) - wall.width / 2) < eps ||
    Math.abs(Math.abs(y) - wall.height / 2) < eps ||
    Math.abs(Math.abs(z) - wall.thickness / 2) < eps
  );
}

function pointSegDist(
  px: number,
  py: number,
  pz: number,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const apx = px - ax;
  const apy = py - ay;
  const apz = pz - az;
  const ab2 = abx * abx + aby * aby + abz * abz;
  const t = ab2 < 1e-12 ? 0 : Math.min(1, Math.max(0, (apx * abx + apy * aby + apz * abz) / ab2));
  return Math.hypot(apx - abx * t, apy - aby * t, apz - abz * t);
}

function interiorEdgeAo(
  rest: [number, number, number],
  restFace: number[][],
  wall: { width: number; height: number; thickness: number },
  falloff: number,
): number {
  let minDist = Infinity;
  const n = restFace.length;
  for (let i = 0; i < n; i += 1) {
    const a = restFace[i];
    const b = restFace[(i + 1) % n];
    const mx = (a[0] + b[0]) * 0.5;
    const my = (a[1] + b[1]) * 0.5;
    const mz = (a[2] + b[2]) * 0.5;
    if (!onWallSkin(a[0], a[1], a[2], wall) || !onWallSkin(b[0], b[1], b[2], wall) || !onWallSkin(mx, my, mz, wall)) {
      continue;
    }
    minDist = Math.min(minDist, pointSegDist(rest[0], rest[1], rest[2], a[0], a[1], a[2], b[0], b[1], b[2]));
  }
  if (!Number.isFinite(minDist)) return 1;
  const t = Math.min(1, Math.max(0, minDist / Math.max(falloff, 1e-4)));
  const s = t * t * (3 - 2 * t);
  return 0.58 + 0.42 * s;
}

/** Overwrite BoxGeometry (or any box-face mesh) UVs so they sample the packed face atlas. */
export function assignWallBoxAtlasUvs(
  geometry: THREE.BufferGeometry,
  wall: { width: number; height: number; thickness: number },
  regions: AtlasRegions,
): void {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const rest = geometry.getAttribute(SURFACE_REST_POSITION_ATTRIBUTE) ?? position;
  const uv = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i += 1) {
    const face = boxFaceFromNormal(normal.getX(i), normal.getY(i), normal.getZ(i));
    const bake = bakeFaceFromBoxFace(face);
    const [fu, fv] = restToFaceUv(rest.getX(i), rest.getY(i), rest.getZ(i), face, wall);
    const [au, av] = atlasUvFromFace(fu, fv, regions[bake]);
    uv[i * 2] = au;
    uv[i * 2 + 1] = av;
  }
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
}

/**
 * Collider points inset toward the centroid so tiling neighbours start clear
 * of each other. Visual meshes keep full-size cell.vertices; only the physics
 * hull uses these. factor === 1 is the unshrunk centroid-relative identity.
 */
export function shrinkHullPoints(
  vertices: number[][],
  centroid: { x: number; y: number; z: number },
  factor: number,
): Array<readonly [number, number, number]> {
  const out: Array<readonly [number, number, number]> = new Array(vertices.length);
  for (let i = 0; i < vertices.length; i += 1) {
    const v = vertices[i];
    out[i] = [
      (v[0] - centroid.x) * factor,
      (v[1] - centroid.y) * factor,
      (v[2] - centroid.z) * factor,
    ] as const;
  }
  return out;
}

export interface CellGeometryOptions {
  /** Subtract this from every vertex (typically the cell centroid so mesh is local). */
  origin?: Vec3;
  wall?: { width: number; height: number; thickness: number };
  atlas?: AtlasRegions;
  interiorTileMeters?: number;
  edgeAoFalloff?: number;
}

/**
 * Give non-fractured geometry the same stable material coordinates that
 * fracture cells carry. The source geometry must already be in wall-local
 * space (for the intact wall that is BoxGeometry before its mesh is offset).
 */
export function addSurfaceRestPositionAttribute(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute('position');
  if (!position || position.itemSize < 3) {
    throw new TypeError('Surface rest positions require a position attribute with xyz coordinates');
  }

  const restPositions = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i += 1) {
    const offset = i * 3;
    restPositions[offset] = position.getX(i);
    restPositions[offset + 1] = position.getY(i);
    restPositions[offset + 2] = position.getZ(i);
  }
  geometry.setAttribute(
    SURFACE_REST_POSITION_ATTRIBUTE,
    new THREE.Float32BufferAttribute(restPositions, 3),
  );
}

/**
 * Flat normal for a convex polygon (sum of fan cross products). Outer
 * (original box surface) faces snap to the nearest axis unit vector so
 * coplanar adjacent cells share IDENTICAL normals (hairline cracks).
 */
function flatFaceNormal(faceVerts: number[][], snapAxis: boolean): [number, number, number] {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  const v0 = faceVerts[0];
  for (let i = 1; i < faceVerts.length - 1; i += 1) {
    const v1 = faceVerts[i];
    const v2 = faceVerts[i + 1];
    nx += (v1[1] - v0[1]) * (v2[2] - v0[2]) - (v1[2] - v0[2]) * (v2[1] - v0[1]);
    ny += (v1[2] - v0[2]) * (v2[0] - v0[0]) - (v1[0] - v0[0]) * (v2[2] - v0[2]);
    nz += (v1[0] - v0[0]) * (v2[1] - v0[1]) - (v1[1] - v0[1]) * (v2[0] - v0[0]);
  }
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;

  if (snapAxis) {
    const ax = Math.abs(nx);
    const ay = Math.abs(ny);
    const az = Math.abs(nz);
    if (ax >= ay && ax >= az) return [nx >= 0 ? 1 : -1, 0, 0];
    if (ay >= ax && ay >= az) return [0, ny >= 0 ? 1 : -1, 0];
    return [0, 0, nz >= 0 ? 1 : -1];
  }
  return [nx, ny, nz];
}

/**
 * Build a BufferGeometry for a single FractureCell.
 * Vertices are offset by `origin` (default: cell.centroid) so the mesh sits
 * at the body transform origin.
 */
function pushFaceAttributes(
  restFaceVerts: number[][],
  faceVerts: number[][],
  isOuter: boolean,
  nx: number,
  ny: number,
  nz: number,
  wall: { width: number; height: number; thickness: number },
  atlas: AtlasRegions,
  tileMeters: number,
  falloff: number,
  positions: number[],
  restPositions: number[],
  normals: number[],
  uvs: number[],
  colors: number[],
  outerIndices: number[],
  innerIndices: number[],
  vertexCount: number,
): number {
  const boxFace = boxFaceFromNormal(nx, ny, nz);
  const bakeFace = bakeFaceFromBoxFace(boxFace);
  const region = atlas[bakeFace];
  for (let i = 1; i < faceVerts.length - 1; i += 1) {
    const tri = [faceVerts[0], faceVerts[i], faceVerts[i + 1]];
    const restTri = [restFaceVerts[0], restFaceVerts[i], restFaceVerts[i + 1]];
    const base = vertexCount;
    for (let vi = 0; vi < tri.length; vi += 1) {
      const p = tri[vi];
      const rest = restTri[vi];
      positions.push(p[0], p[1], p[2]);
      restPositions.push(rest[0], rest[1], rest[2]);
      normals.push(nx, ny, nz);
      if (isOuter) {
        const [fu, fv] = restToFaceUv(rest[0], rest[1], rest[2], boxFace, wall);
        const [au, av] = atlasUvFromFace(fu, fv, region);
        uvs.push(au, av);
        colors.push(1, 1, 1);
      } else {
        const [iu, iv] = restToInteriorUv(rest[0], rest[1], rest[2], nx, ny, nz, tileMeters);
        uvs.push(iu, iv);
        const ao = interiorEdgeAo(
          [rest[0], rest[1], rest[2]],
          restFaceVerts,
          wall,
          falloff,
        );
        colors.push(ao, ao, ao);
      }
      vertexCount += 1;
    }
    const target = isOuter ? outerIndices : innerIndices;
    target.push(base, base + 1, base + 2);
  }
  return vertexCount;
}

function finishCellGeometry(
  positions: number[],
  restPositions: number[],
  normals: number[],
  uvs: number[],
  colors: number[],
  outerIndices: number[],
  innerIndices: number[],
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute(
    SURFACE_REST_POSITION_ATTRIBUTE,
    new THREE.Float32BufferAttribute(restPositions, 3),
  );
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const allIndices = outerIndices.concat(innerIndices);
  geometry.setIndex(allIndices);
  geometry.clearGroups();
  if (outerIndices.length > 0) {
    geometry.addGroup(0, outerIndices.length, 0);
  }
  if (innerIndices.length > 0) {
    geometry.addGroup(outerIndices.length, innerIndices.length, 1);
  }
  if (allIndices.length >= 3) {
    try {
      geometry.computeTangents();
    } catch {
      // Degenerate cells can lack a valid tangent frame.
    }
  }
  if (!geometry.getAttribute('tangent')) {
    const n = geometry.getAttribute('position').count;
    const tangents = new Float32Array(n * 4);
    for (let i = 0; i < n; i += 1) {
      tangents[i * 4] = 1;
      tangents[i * 4 + 3] = 1;
    }
    geometry.setAttribute('tangent', new THREE.BufferAttribute(tangents, 4));
  }
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Build a BufferGeometry for a single FractureCell.
 * Vertices are offset by `origin` (default: cell.centroid) so the mesh sits
 * at the body transform origin.
 */
export function cellToBufferGeometry(
  cell: FractureCell,
  options: CellGeometryOptions = {},
): THREE.BufferGeometry {
  const origin = options.origin ?? cell.centroid;
  const wall = options.wall ?? DEFAULT_WALL_SIZE;
  const atlas = options.atlas ?? IDENTITY_ATLAS;
  const tileMeters = options.interiorTileMeters ?? INTERIOR_TILE_METERS;
  const falloff = options.edgeAoFalloff ?? EDGE_AO_FALLOFF_M;
  const positions: number[] = [];
  const restPositions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const outerIndices: number[] = [];
  const innerIndices: number[] = [];

  let vertexCount = 0;

  for (let fi = 0; fi < cell.faces.length; fi += 1) {
    const face = cell.faces[fi];
    if (face.length < 3) continue;

    const isOuter = cell.outerFaceFlags[fi] === true;
    const restFaceVerts = face.map((idx) => cell.vertices[idx]);
    const faceVerts: number[][] = restFaceVerts.map((v) => {
      return [v[0] - origin.x, v[1] - origin.y, v[2] - origin.z];
    });

    const [nx, ny, nz] = flatFaceNormal(faceVerts, isOuter);
    vertexCount = pushFaceAttributes(
      restFaceVerts,
      faceVerts,
      isOuter,
      nx,
      ny,
      nz,
      wall,
      atlas,
      tileMeters,
      falloff,
      positions,
      restPositions,
      normals,
      uvs,
      colors,
      outerIndices,
      innerIndices,
      vertexCount,
    );
  }

  return finishCellGeometry(
    positions,
    restPositions,
    normals,
    uvs,
    colors,
    outerIndices,
    innerIndices,
  );
}

/**
 * Merge many cells into ONE BufferGeometry (for the static remnant).
 * Each cell is placed at worldOffset + cell.centroid (vertices already relative
 * to cell centroid when built; we pass world positions of vertices).
 *
 * `worldOffset` is added to every cell vertex (e.g. wall center).
 */
export function mergeCellsToBufferGeometry(
  cells: FractureCell[],
  worldOffset: Vec3 = { x: 0, y: 0, z: 0 },
  options: CellGeometryOptions = {},
): THREE.BufferGeometry {
  const wall = options.wall ?? DEFAULT_WALL_SIZE;
  const atlas = options.atlas ?? IDENTITY_ATLAS;
  const tileMeters = options.interiorTileMeters ?? INTERIOR_TILE_METERS;
  const falloff = options.edgeAoFalloff ?? EDGE_AO_FALLOFF_M;
  const positions: number[] = [];
  const restPositions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const outerIndices: number[] = [];
  const innerIndices: number[] = [];
  let vertexCount = 0;

  for (const cell of cells) {
    for (let fi = 0; fi < cell.faces.length; fi += 1) {
      const face = cell.faces[fi];
      if (face.length < 3) continue;
      const isOuter = cell.outerFaceFlags[fi] === true;

      const restFaceVerts = face.map((idx) => cell.vertices[idx]);
      const faceVerts: number[][] = restFaceVerts.map((v) => {
        return [v[0] + worldOffset.x, v[1] + worldOffset.y, v[2] + worldOffset.z];
      });

      const [nx, ny, nz] = flatFaceNormal(faceVerts, isOuter);
      vertexCount = pushFaceAttributes(
        restFaceVerts,
        faceVerts,
        isOuter,
        nx,
        ny,
        nz,
        wall,
        atlas,
        tileMeters,
        falloff,
        positions,
        restPositions,
        normals,
        uvs,
        colors,
        outerIndices,
        innerIndices,
        vertexCount,
      );
    }
  }

  return finishCellGeometry(
    positions,
    restPositions,
    normals,
    uvs,
    colors,
    outerIndices,
    innerIndices,
  );
}
